import { useCallback } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { isNumber } from 'lodash';
import { useSelector } from 'react-redux';
import Text from '../../../../../components/Text';
import { conditional, initValue } from '../../../../../utils';
import FemaleIcon from '../../../../../static/images/GENDER_FEMALE.png';
import MaleIcon from '../../../../../static/images/GENDER_MALE.png';
import XmaleIcon from '../../../../../static/images/GENDER_X.png';
import { eadmissionAPI } from '../../../../../apis';
import StyleButton from '../../../../../components/StyleButton';
import { Triangle } from '../../../../Admission/components/Triangle';
import { EditUI } from '../../../../Admission/components/BusStop/components';
import { getPriorityInfo, WtBg } from '../../../../Admission/components/BusStop/components/Row';
import { i18n } from '../../../../../language';
import ElderlyHomeInfo from '../../../../../components/ElderlyHomeInfo';

const useStyles = makeStyles((theme) => ({
  sup: {
    fontWeight: '700',
    color: '#538FFF',
    fontSize: '0.75rem',
    fontFamily: 'NotoSans-Medium',
    marginLeft: '0.25rem',
  },
  busStop: {
    '&.MuiTableCell-root': {
      padding: '0 !important',
      borderTop: 'none !important',
    },
  },
  admit: {
    color: '#7D86A9',
    padding: '0.08rem 0.35rem',
    fontSize: '0.95rem',
    whiteSpace: 'pre',
    borderRadius: '0.25rem',
    backgroundColor: '#F5FAFE',
  },
  genderIcon: ({ iconSize, genderColor }) => ({
    width: iconSize,
    color: genderColor,
  }),
  age: ({ iconBoxSize, genderColor }) => ({
    color: genderColor,
    width: iconBoxSize,
    fontSize: '1rem',
    fontFamily: 'NotoSans-Bold',
    textAlign: 'center',
  }),
}));

const getStatusColor = (status) => {
  switch (status) {
    case 'PFYA':
    case 'P':
    case 'PWA':
    case 'PWR':
      return '#FA8A82';
    case 'O':
      return '#7FD6F1';
    case 'A':
    case 'DL':
    case 'WR':
    case 'RWR':
    case 'REDIR':
      return '#FFFFFF';
    case 'C':
      return '#28D2AB';
    case 'D':
      return '#878687';
    case 'H':
      return '#A6B0C3';
    default:
      return '';
  }
};

const getGenderColor = (gender) => {
  switch (gender) {
    case 'F':
      return '#FF8181';
    case 'M':
      return '#538FFF';
    case 'X':
      return '#7B80FF';

    default:
      return '';
  }
};

const getStatusCw = (status) => {
  switch (status) {
    case 'PFYA':
      return 'Pending FYA';
    case 'P':
      return 'Pending';
    case 'PWA':
      return 'Pending Ward Ack';
    case 'PWR':
      return 'Pending Ward Ready';
    case 'O':
      return 'Pending Adm.';
    case 'A':
      return 'Accepted';
    case 'RWR':
      return i18n.t('BEDSTATUS_RECV_WARD_READY');
    case 'WR':
      return i18n.t('BEDSTATUS_WARD_READY');
    case 'DL':
      return 'Declined';
    case 'REDIR':
      return 'Redirected';
    case 'C':
      return 'Completed';
    case 'D':
      return 'Withdrawn';
    case 'H':
      return 'Hold';
    default:
      return '';
  }
};

export const PatientUI = ({ iconBoxSize = '3.3rem', caseNo, name, gender, age, hkid = '', ehAddrInfo }) => {
  const getGenderIconSize = () => {
    switch (gender) {
      case 'F':
        return '1.85rem';
      case 'M':
        return '2.3rem';
      default:
        return '1.7rem';
    }
  };
  const classes = useStyles({ iconSize: getGenderIconSize(), iconBoxSize, genderColor: getGenderColor(gender) });

  return (
    <Box display="inline-flex" gridGap="0.62rem">
      <Box>
        <Box display="flex" alignItems="center" justifyContent="center">
          {gender === 'M' && <img className={classes.genderIcon} src={MaleIcon} alt="MaleIcon" />}
          {gender === 'F' && <img className={classes.genderIcon} src={FemaleIcon} alt="FemaleIcon" />}
          {gender === 'X' && <img className={classes.genderIcon} src={XmaleIcon} alt="XmaleIcon" />}
        </Box>
        <div className={classes.age}>{initValue(age, '')}</div>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="0.25rem">
        {caseNo && (
          <Text fontFamily="NotoSans-Bold" color={getGenderColor(gender)}>
            {initValue(caseNo, '')}
          </Text>
        )}
        {hkid && (
          <Text fontFamily="NotoSans-Bold" color={getGenderColor(gender)}>
            {initValue(hkid, '')}
          </Text>
        )}
        <Box display="flex" gridGap="0.714rem">
          <ElderlyHomeInfo ehAddrInfo={ehAddrInfo} />
          <Text color="#2E4469" fontSize="16px" fontFamily="HA_MingLiu, NotoSans-Medium">
            {initValue(name, '')}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

const getHighlight = (status) => {
  switch (status) {
    case 'PWA':
    case 'PWR':
      return '#FA8A82';
    case 'O':
      return '#7FD6F1';
    case 'REDIR':
      return '#878687';
    case 'C':
      return '#34C759';
    case 'D':
      return '#878687';
    case 'H':
      return '#A6B0C3';
    default:
      return '';
  }
};

const Row = (props) => {
  const {
    eaId,
    targetArriveDtm,
    patient,
    priority,
    status,
    diagnosis,
    remark,
    columnIndex,
    showDetail,
    transferRequestId,
    admissionType,
    eisSpecCd,
    fromHospCode,
    fromWardLocation,
    fromBedNo,
    assignedBedNo,
    openEdit,
    bedstatus,
    handleEditEADM,
    setSuccessAlert,
    handleUpdateSingleData,
    busStopInfo,
    hold,
    patGender,
    version,
    handleAdmissionVersionError,
    admitWard,
    admitSpecialtyOthers,
    specialtyRemark,
    setIsShowLoginPop = () => {},
    wardLocation,
    wardCd,
    hkid,
    ehAddrInfo,
  } = props;
  const history = useHistory();
  const classes = useStyles();
  const isLogin = useSelector((state) => state.loginInfo.flag);

  const handleEdit = useCallback(() => {
    if (admissionType === 'EADM') {
      if (bedstatus) {
        handleEditEADM();
        history.push(`/bedstatusForm/${eaId}?transferRequestId=${transferRequestId}`);
      } else {
        history.push(`/admissionForm/${eaId}?transferRequestId=${transferRequestId}`);
      }
    } else {
      openEdit && openEdit(props);
    }
  }, [eaId, transferRequestId, openEdit, props]);

  const handleConfirmReady = useCallback(async () => {
    if (!isLogin) {
      setIsShowLoginPop(true);
      return;
    }

    try {
      const params = {
        actionType: 9,
        eaId,
        transferRequestId,
        version,
      };
      await eadmissionAPI.editAdmission(params);
      handleUpdateSingleData(transferRequestId);
      setSuccessAlert(true);
    } catch (error) {
      handleAdmissionVersionError(error);
    }
  }, [eaId, transferRequestId, handleUpdateSingleData]);

  switch (columnIndex) {
    case 0:
      return (
        <Box display="flex" flexDirection="column" gridGap="0.62rem">
          <Box display="flex" alignItems="center" gridGap="0.62rem">
            <Box
              width="0.625rem"
              height="0.625rem"
              minWidth="0.625rem"
              minHeight="0.625rem"
              borderRadius="50%"
              bgcolor={getStatusColor(status)}
              border={`1px solid ${conditional(
                getStatusColor(status) === '#FFFFFF',
                '#AAAAAA',
                getStatusColor(status)
              )}`}
            />
            {getStatusCw(status)}
          </Box>
          {status === 'PWR' && (
            <StyleButton
              {...{
                text: 'Ward Ready',
                backgroundColor: '#28D2AB',
                type: 'fill',
              }}
              p="0.88rem"
              width="auto"
              onClick={handleConfirmReady}
            />
          )}
        </Box>
      );
    case 1:
      return (
        <Box display="inline-flex">
          <Box
            display="flex"
            p="0.625rem"
            borderRadius="4px"
            bgcolor={WtBg(moment(targetArriveDtm).diff(moment(), 'seconds'))}
            color="#FFFFFF"
          >
            {moment(targetArriveDtm).format('YYYY-MM-DD HH:mm')}
          </Box>
        </Box>
      );
    case 2:
      return admissionType === 'EADM' ? (
        <Box display="flex">{isNumber(priority) && <Triangle {...getPriorityInfo(priority)} />}</Box>
      ) : (
        admissionType
      );
    case 3:
      return <PatientUI iconBoxSize="2rem" {...patient} hkid={hkid} ehAddrInfo={ehAddrInfo} />;
    case 4:
      return (
        <Box display="flex" flexDirection="column" gridGap="0.5rem">
          <Box>
            {[eisSpecCd, fromHospCode, fromWardLocation, fromBedNo].map((item) => initValue(item, '--')).join('/')}
          </Box>
          {admissionType === 'EADM' && (
            <Box>
              <Box display="flex" gridGap="0.3rem" mb="0.3rem">
                {admitWard && <div className={classes.admit}>{initValue(admitWard, '   ')}</div>}
                {admitSpecialtyOthers && <div className={classes.admit}>{initValue(admitSpecialtyOthers, '   ')}</div>}
              </Box>
              <Text fontSize="0.95rem" color="#B92B21">
                {initValue(specialtyRemark, '')}
              </Text>
            </Box>
          )}
        </Box>
      );
    case 5:
      return initValue(diagnosis, '--');
    case 6:
      return initValue(remark, '--');
    case 7:
      return initValue(`${wardLocation ?? '--'} ${wardCd ?? '--'} / ${assignedBedNo ?? '--'}`, '');
    case 8:
      return (
        <EditUI
          open={showDetail}
          handleEdit={handleEdit}
          admissionType={admissionType}
          busStopInfo={busStopInfo}
          hold={hold}
          gender={patGender}
          eaId={eaId}
        />
      );
    default:
      return <div />;
  }
};

export default Row;
